/**
 * 全局 UI 色彩 token。
 *
 * 新增 UI 代码优先使用 MUI 语义色(text.primary / divider / primary.main 等)；
 * 确需直接传颜色字符串时，从这里取值，避免在页面或组件里手写 hex / rgba。
 *
 * 现存历史颜色不在本文件创建时一次性迁移，后续按公共组件与模块重构逐步收口。
 */
export const UI_BRAND_COLORS = {
  primary: "#2563eb",
  primaryLight: "#4f7eff",
  primaryDark: "#1d4ed8",
  secondary: "#0f766e",
} as const;

export const UI_TEXT_COLORS = {
  primary: "#334155",
  secondary: "rgba(51,65,85,0.68)",
  tertiary: "rgba(51,65,85,0.46)",
  disabled: "rgba(51,65,85,0.38)",
  inverse: "#ffffff",
} as const;

export const UI_BACKGROUND_COLORS = {
  default: "#f5f7fb",
  paper: "#ffffff",
  field: "rgba(248,250,252,0.88)",
} as const;

export const UI_BORDER_COLORS = {
  subtle: "rgba(51,65,85,0.08)",
  default: "rgba(51,65,85,0.10)",
  medium: "rgba(51,65,85,0.18)",
} as const;

export const UI_ACTION_COLORS = {
  hover: "rgba(51,65,85,0.04)",
  disabledBackground: "rgba(51,65,85,0.04)",
  selectedPrimary: "rgba(37,99,235,0.10)",
} as const;

export const UI_TABLE_COLORS = {
  rowOdd: "rgba(51,65,85,0.025)",
  rowHover: "rgba(37,99,235,0.07)",
} as const;

export const UI_SKELETON_COLORS = {
  base: "rgba(51,65,85,0.12)",
  wave: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
} as const;

export const UI_SHADOWS = {
  card: "0 10px 32px rgba(51,65,85,0.05)",
  dropdown: "0 14px 36px rgba(51,65,85,0.18)",
  tooltip: "0 12px 32px rgba(51,65,85,0.20)",
} as const;

export const UI_FEEDBACK_COLORS = {
  info: "#2563eb",
  success: "#16a34a",
  warning: "#f59e0b",
  error: "#f87171",
} as const;

export const UI_USER_AVATAR_COLORS = {
  male: {
    text: "#2563eb",
    background: "#eff6ff",
    border: "rgba(37,99,235,0.16)",
  },
  female: {
    text: "#be185d",
    background: "#fdf2f8",
    border: "rgba(190,24,93,0.14)",
  },
  neutral: {
    text: "#64748b",
    background: "#f8fafc",
    border: "rgba(51,65,85,0.12)",
  },
} as const;

/** 计调标准资源列表的稳定视觉语义。 */
export const UI_STANDARD_RESOURCE_COLORS = {
  tableHeaderText: "#475569",
  statusActiveText: "#2f7d4f",
  statusActiveBackground: "#f3faf5",
  statusActiveBorder: "#cfe8d8",
  actionEdit: "#52677f",
  actionDanger: "#9f4b4b",
  actionSuccess: "#2f7d4f",
} as const;

/** 后台小型分类 Chip 的通用弱色池。各模块按自身语义取子集。 */
export const UI_CHIP_CATEGORY_COLORS = {
  positive: { text: "#3f7f5f", background: "#f4faf6", border: "#cfe8d8" },
  warning: { text: "#9a6a22", background: "#fff9ed", border: "#ead7a8" },
  neutral: { text: "#64748b", background: "#f8fafc", border: "#cbd5e1" },
  exception: { text: "#9f4b4b", background: "#fff5f5", border: "#e8c4c4" },
  purple: { text: "#6f5a9e", background: "#f8f5ff", border: "#ded3f2" },
  cyan: { text: "#27758a", background: "#f3fafc", border: "#c7e2e8" },
  indigo: { text: "#4f5fb3", background: "#f4f6ff", border: "#ccd5f6" },
  blue: { text: "#2563eb", background: "#eff6ff", border: "#bfdbfe" },
  blueSubtle: { text: "#476da4", background: "#f5f8fc", border: "#c7d7ee" },
  brown: { text: "#7a6040", background: "#fbf7f0", border: "#e2d3bd" },
  orange: { text: "#c45a11", background: "#fff7ed", border: "#fed7aa" },
} as const;

/** 部门底牌使用同色系较清晰的底色，不改变角色和状态 Chip。 */
export const UI_DEPARTMENT_PLATE_BACKGROUNDS = {
  blue: UI_CHIP_CATEGORY_COLORS.blue.border,
  purple: UI_CHIP_CATEGORY_COLORS.purple.border,
  cyan: UI_CHIP_CATEGORY_COLORS.cyan.border,
  brown: UI_CHIP_CATEGORY_COLORS.brown.border,
  neutral: UI_CHIP_CATEGORY_COLORS.neutral.border,
} as const;

/** 浮动文字角标的实底色。用于 2-4 字小气泡,需要比弱 Chip 更高对比度。 */
export const UI_FLOATING_BADGE_COLORS = {
  exception: "#b91c1c",
  warning: "#d97706",
  neutral: "#475569",
  monochrome: "#000000",
} as const;

/**
 * 计调资源中“程度 / 等级 / 例外”类标签的统一语义色。
 * 源自已验收的景区游玩强度与住宿便利程度，不用于名称旁的资源类型标签。
 */
export const UI_STANDARD_RESOURCE_SEMANTIC_CHIP_COLORS = {
  positive: UI_CHIP_CATEGORY_COLORS.positive,
  warning: UI_CHIP_CATEGORY_COLORS.warning,
  neutral: UI_CHIP_CATEGORY_COLORS.neutral,
  exception: UI_CHIP_CATEGORY_COLORS.exception,
} as const;

/** 用户生命周期状态标签色。 */
export const UI_USER_STATUS_CHIP_COLORS = {
  ACTIVE: {
    text: UI_STANDARD_RESOURCE_COLORS.statusActiveText,
    background: UI_STANDARD_RESOURCE_COLORS.statusActiveBackground,
    border: UI_STANDARD_RESOURCE_COLORS.statusActiveBorder,
  },
  DISABLED: UI_CHIP_CATEGORY_COLORS.neutral,
  LEFT: UI_CHIP_CATEGORY_COLORS.exception,
} as const;

/** 询单中心排班表早 / 晚 / 休班次语义色。 */
export const UI_SCHEDULE_SHIFT_COLORS = {
  early: {
    text: "#0f766e",
    background: "rgba(15,118,110,0.15)",
    backgroundSubtle: "rgba(15,118,110,0.10)",
    border: "rgba(15,118,110,0.30)",
    borderSubtle: "rgba(15,118,110,0.22)",
  },
  late: {
    text: "#b45309",
    background: "rgba(217,119,6,0.16)",
    backgroundSubtle: "rgba(217,119,6,0.10)",
    border: "rgba(217,119,6,0.32)",
    borderSubtle: "rgba(217,119,6,0.22)",
  },
  off: {
    text: "#475569",
    background: "rgba(100,116,139,0.14)",
    backgroundSubtle: "rgba(100,116,139,0.10)",
    border: "rgba(100,116,139,0.30)",
    borderSubtle: "rgba(100,116,139,0.22)",
  },
  leave: {
    text: "#9a6a22",
    background: "rgba(245,158,11,0.14)",
    backgroundSubtle: "rgba(245,158,11,0.09)",
    border: "rgba(245,158,11,0.30)",
    borderSubtle: "rgba(245,158,11,0.20)",
  },
} as const;

/**
 * GMV 挂牌固定配色：保存业务方在预览中调好的色值。
 * 第二、第三名按确认互换；农夫独立于排名。
 */
export const GMV_SCOREBOARD_KEYS = ["first", "second", "third", "farmer"] as const;
export type GmvScoreboardKey = (typeof GMV_SCOREBOARD_KEYS)[number];
export const UI_GMV_SCOREBOARD_COLORS: Record<GmvScoreboardKey, { panel: string; digit: string }> = {
  first: { panel: "#f00f0f", digit: UI_TEXT_COLORS.inverse },
  second: { panel: "#b2c520", digit: UI_TEXT_COLORS.inverse },
  third: { panel: "#020203", digit: UI_TEXT_COLORS.inverse },
  farmer: { panel: "#3f7f5f", digit: UI_TEXT_COLORS.inverse },
};
